/**
 * AI Memory Service
 *
 * Compiles context from all 4 memory layers and provides
 * APIs for managing AI memories and patterns.
 *
 * All data is persisted via the centralized AI Memory API
 * (port 3500, accessed through the backend gateway).
 * No localStorage is used for business data.
 */

import {
  fetchBaseInstructions as apiFetchBaseInstructions,
  fetchSectors as apiFetchSectors,
  fetchSectorInstructions as apiFetchSectorInstructions,
  fetchPatterns as apiFetchPatterns,
  fetchUserPreferences as apiFetchUserPreferences,
  savePatternApi,
  approvePatternApi,
  rejectPatternApi,
  saveUserPreferenceApi,
  compileContextApi,
  fetchMemoryStats,
} from './memoryApiClient';
import type {
  AICompiledContext,
  AIBaseInstruction,
  AISector,
  AISectorInstruction,
  AICollectivePattern,
  AIUserPreference,
  AIUserSector,
  CreatePatternInput,
  ReviewPatternInput,
  CreateUserPreferenceInput,
  RecordCorrectionInput,
  AdminDashboardStats,
  AIPatternType,
} from './types';

// =============================================================================
// CONTEXT COMPILATION
// =============================================================================

/**
 * Compile full AI context for a user session.
 * Combines all 4 layers in priority order.
 */
export async function compileContext(userId: string): Promise<AICompiledContext> {
  const compiledAt = new Date().toISOString();

  // Fetch all layers in parallel
  const [
    baseInstructions,
    userSectors,
    userPreferences,
  ] = await Promise.all([
    fetchBaseInstructions(),
    fetchUserSectors(userId),
    fetchUserPreferences(userId),
  ]);

  // Get sector instructions and patterns based on user's sectors
  const primarySectorId = userSectors.find(s => s.isPrimary)?.sectorId
    || userSectors[0]?.sectorId;

  const sectorIds = userSectors.map(s => s.sectorId);

  const [sectorInstructions, collectivePatterns] = await Promise.all([
    fetchSectorInstructions(sectorIds),
    fetchCollectivePatterns(sectorIds),
  ]);

  // Compile into final context
  return {
    userId,
    primarySectorId,
    baseInstructions: baseInstructions
      .sort((a, b) => a.priority - b.priority)
      .map(i => i.instructionText),
    sectorInstructions: sectorInstructions
      .sort((a, b) => a.priority - b.priority)
      .map(i => i.instructionText),
    collectivePatterns: collectivePatterns.map(p => ({
      type: p.patternType,
      key: p.patternKey,
      value: p.patternValue,
    })),
    userPreferences: userPreferences.map(p => ({
      type: p.preferenceType,
      key: p.preferenceKey,
      value: p.preferenceValue,
    })),
    compiledAt,
    layersIncluded: [1, 2, 3, 4],
  };
}

/**
 * Build system prompt from compiled context.
 * This is what gets sent to the AI model.
 */
export function buildSystemPromptFromContext(context: AICompiledContext): string {
  const sections: string[] = [];

  // Layer 1: Base instructions
  if (context.baseInstructions.length > 0) {
    sections.push(context.baseInstructions.join('\n\n'));
  }

  // Layer 2: Sector-specific instructions
  if (context.sectorInstructions.length > 0) {
    sections.push('--- Conoscenze specifiche del settore ---');
    sections.push(context.sectorInstructions.join('\n\n'));
  }

  // Layer 3: Collective patterns
  if (context.collectivePatterns.length > 0) {
    const patternsByType = groupBy(context.collectivePatterns, 'type');

    sections.push('--- Pattern appresi dalla community ---');

    for (const [type, patterns] of Object.entries(patternsByType)) {
      const label = getPatternTypeLabel(type as AIPatternType);
      sections.push(`${label}:`);
      patterns.forEach(p => {
        sections.push(`- ${p.key}: ${p.value}`);
      });
    }
  }

  // Layer 4: User preferences
  if (context.userPreferences.length > 0) {
    sections.push('--- Preferenze personali dell\'utente ---');
    context.userPreferences.forEach(p => {
      sections.push(`- ${p.key}: ${p.value}`);
    });
  }

  return sections.join('\n\n');
}

// =============================================================================
// LAYER 1: BASE INSTRUCTIONS
// =============================================================================

async function fetchBaseInstructions(): Promise<AIBaseInstruction[]> {
  try {
    return await apiFetchBaseInstructions();
  } catch (error) {
    console.warn('[MemoryService] Failed to fetch base instructions:', error);
    return [];
  }
}

// =============================================================================
// LAYER 2: SECTORS
// =============================================================================

async function fetchUserSectors(_userId: string): Promise<AIUserSector[]> {
  // User-sector assignments are managed via the memory API compile endpoint.
  // For now return empty — the compile API handles sector resolution.
  return [];
}

async function fetchSectorInstructions(sectorIds: string[]): Promise<AISectorInstruction[]> {
  if (sectorIds.length === 0) return [];

  try {
    // Fetch instructions for each sector
    const results = await Promise.all(
      sectorIds.map(id => apiFetchSectorInstructions(id))
    );
    return results.flat();
  } catch (error) {
    console.warn('[MemoryService] Failed to fetch sector instructions:', error);
    return [];
  }
}

export async function fetchAllSectors(): Promise<AISector[]> {
  try {
    return await apiFetchSectors();
  } catch (error) {
    console.warn('[MemoryService] Failed to fetch sectors:', error);
    return [];
  }
}

// =============================================================================
// LAYER 3: COLLECTIVE PATTERNS
// =============================================================================

async function fetchCollectivePatterns(sectorIds: string[]): Promise<AICollectivePattern[]> {
  try {
    // Fetch approved patterns, optionally filtered by sector
    const sectorId = sectorIds.length === 1 ? sectorIds[0] : undefined;
    return await apiFetchPatterns('approved', sectorId);
  } catch (error) {
    console.warn('[MemoryService] Failed to fetch collective patterns:', error);
    return [];
  }
}

/**
 * Record a new pattern detected from user interaction.
 * This creates a pending pattern for admin review.
 */
export async function recordPattern(
  input: CreatePatternInput,
  _userId: string
): Promise<AICollectivePattern | null> {
  try {
    const pattern: AICollectivePattern = {
      id: '',
      sectorId: input.sectorId,
      sourceType: input.sourceType,
      patternType: input.patternType,
      patternKey: input.patternKey,
      patternValue: input.patternValue,
      contextDescription: input.contextDescription,
      occurrenceCount: 1,
      lastOccurrenceAt: new Date().toISOString(),
      confidenceScore: 50,
      approvalStatus: 'pending',
      isActive: false,
      autoApproveThreshold: 85,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    return await savePatternApi(pattern);
  } catch (error) {
    console.warn('[MemoryService] Failed to record pattern:', error);
    return null;
  }
}

/**
 * Reinforce an existing pattern (increase confidence).
 */
export async function reinforcePattern(
  _patternId: string,
  _userId: string,
  _success: boolean
): Promise<void> {
  // Reinforcement is handled server-side via the record endpoint
}

// =============================================================================
// LAYER 4: USER PREFERENCES
// =============================================================================

async function fetchUserPreferences(userId: string): Promise<AIUserPreference[]> {
  try {
    return await apiFetchUserPreferences(userId);
  } catch (error) {
    console.warn('[MemoryService] Failed to fetch user preferences:', error);
    return [];
  }
}

/**
 * Save a user preference.
 */
export async function saveUserPreference(
  userId: string,
  input: CreateUserPreferenceInput
): Promise<AIUserPreference | null> {
  try {
    return await saveUserPreferenceApi(userId, {
      id: '',
      preferenceType: input.preferenceType,
      preferenceKey: input.preferenceKey,
      preferenceValue: input.preferenceValue,
      contextDescription: input.contextDescription,
      source: 'manual',
      confidenceScore: 100,
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.warn('[MemoryService] Failed to save user preference:', error);
    return null;
  }
}

/**
 * Record a user correction (for learning).
 */
export async function recordCorrection(
  _userId: string,
  _input: RecordCorrectionInput
): Promise<void> {
  // Corrections are tracked server-side via the memory API feedback endpoint
  // No localStorage fallback needed
}

// =============================================================================
// ADMIN: PATTERN REVIEW
// =============================================================================

export async function getPendingPatterns(): Promise<AICollectivePattern[]> {
  try {
    return await apiFetchPatterns('pending');
  } catch (error) {
    console.warn('[MemoryService] Failed to fetch pending patterns:', error);
    return [];
  }
}

export async function reviewPattern(
  input: ReviewPatternInput,
  _adminUserId: string
): Promise<AICollectivePattern | null> {
  try {
    if (input.action === 'approve') {
      return await approvePatternApi(input.patternId);
    } else {
      return await rejectPatternApi(input.patternId);
    }
  } catch (error) {
    console.warn('[MemoryService] Failed to review pattern:', error);
    return null;
  }
}

// =============================================================================
// ADMIN: DASHBOARD STATS
// =============================================================================

export async function getAdminDashboardStats(): Promise<AdminDashboardStats> {
  try {
    const stats = await fetchMemoryStats();
    return {
      totalPatterns: stats.totalPatterns ?? 0,
      pendingPatterns: stats.pendingPatterns ?? 0,
      approvedPatterns: stats.approvedPatterns ?? 0,
      rejectedPatterns: stats.rejectedPatterns ?? 0,
      autoApprovedPatterns: stats.autoApprovedPatterns ?? 0,
      totalUsers: 0,
      usersWithSector: 0,
      usersWithPreferences: 0,
      totalSectors: stats.totalSectors ?? 0,
      activeSectors: stats.activeSectors ?? 0,
      sectorsWithPatterns: 0,
      totalCorrections: stats.totalCorrections ?? 0,
      processedCorrections: 0,
      avgConfidenceScore: 0,
      patternsLast24h: 0,
      correctionsLast24h: 0,
      preferencesLast24h: 0,
    };
  } catch (error) {
    console.warn('[MemoryService] Failed to fetch dashboard stats:', error);
    return {
      totalPatterns: 0, pendingPatterns: 0, approvedPatterns: 0,
      rejectedPatterns: 0, autoApprovedPatterns: 0,
      totalUsers: 0, usersWithSector: 0, usersWithPreferences: 0,
      totalSectors: 0, activeSectors: 0, sectorsWithPatterns: 0,
      totalCorrections: 0, processedCorrections: 0, avgConfidenceScore: 0,
      patternsLast24h: 0, correctionsLast24h: 0, preferencesLast24h: 0,
    };
  }
}

// =============================================================================
// HELPERS
// =============================================================================

function groupBy<T>(arr: T[], key: keyof T): Record<string, T[]> {
  return arr.reduce((acc, item) => {
    const k = String(item[key]);
    if (!acc[k]) acc[k] = [];
    acc[k].push(item);
    return acc;
  }, {} as Record<string, T[]>);
}

function getPatternTypeLabel(type: AIPatternType): string {
  const labels: Record<AIPatternType, string> = {
    terminology: 'Terminologia',
    workflow: 'Flussi di lavoro',
    response_style: 'Stile di risposta',
    document_format: 'Formato documenti',
    pricing_pattern: 'Pattern di prezzo',
    scheduling: 'Programmazione',
    communication: 'Comunicazione',
    error_resolution: 'Risoluzione problemi',
  };
  return labels[type] || type;
}

// =============================================================================
// EXPORTS
// =============================================================================

export const memoryService = {
  // Context
  compileContext,
  buildSystemPromptFromContext,

  // Sectors
  fetchAllSectors,

  // Patterns
  recordPattern,
  reinforcePattern,
  getPendingPatterns,
  reviewPattern,

  // Preferences
  saveUserPreference,
  recordCorrection,

  // Admin
  getAdminDashboardStats,
};

export default memoryService;
